import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { garageService } from '../../services/api';
import { toast } from '../../hooks/use-toast';
import { Plus, Edit, Trash2, Save, X, MapPin, Phone, Mail, Users, Clock, Key, Building2, User, ExternalLink, CreditCard, Calendar, IndianRupee, RefreshCw, Send, Eye, AlertTriangle, CheckCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GaragesManager = () => {
  const [garages, setGarages] = useState([]);
  const [garageAccounts, setGarageAccounts] = useState([]);
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [allPayments, setAllPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isAccountDialogOpen, setIsAccountDialogOpen] = useState(false);
  const [isPaymentDialogOpen, setIsPaymentDialogOpen] = useState(false);
  const [isPlanDialogOpen, setIsPlanDialogOpen] = useState(false);
  const [selectedGarage, setSelectedGarage] = useState(null);
  const [editingGarage, setEditingGarage] = useState(null);
  const [editingPlan, setEditingPlan] = useState(null);
  
  // Location garage form
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    location: '',
    contact_number: '',
    email: '',
    capacity: 10,
    services_offered: [],
    working_hours: '9:00 AM - 7:00 PM',
    manager_name: '',
    is_active: true
  });

  // Garage account form (for Propitstop Lite login)
  const [accountFormData, setAccountFormData] = useState({
    name: '',
    email: '',
    password: '',
    auto_generate_password: false,
    send_email: true,
    mobile: '',
    address: '',
    city: 'Bangalore',
    gst_number: '',
    owner_name: '',
    owner_pin: '1234',
    subscription_plan: 'monthly',
    subscription_amount: 2999
  });
  
  // Payment form
  const [paymentFormData, setPaymentFormData] = useState({
    amount: '',
    payment_date: new Date().toISOString().split('T')[0],
    payment_method: 'offline',
    reference_number: '',
    notes: '',
    extend_subscription: true,
    extension_days: 30
  });
  
  // Plan form
  const [planFormData, setPlanFormData] = useState({
    plan_name: '',
    amount: 0,
    duration_days: 30,
    features: []
  });

  useEffect(() => {
    fetchData();
    fetchSubscriptionPlans();
    fetchAllPayments();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const data = await garageService.getAll();
      setGarages(data.garages || []);
      setGarageAccounts(data.garage_accounts || []);
    } catch (error) {
      console.error('Error fetching garages:', error);
      toast({
        title: 'Error',
        description: 'Failed to load garages',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchGarageAccounts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/garages/accounts`);
      const data = await response.json();
      if (data.success) {
        setGarageAccounts(data.accounts || []);
      }
    } catch (error) {
      console.error('Error fetching garage accounts:', error);
    }
  };
  
  const fetchSubscriptionPlans = async () => {
    try {
      const response = await fetch(`${API_URL}/api/garages/subscription-plans`);
      const data = await response.json();
      if (data.success) {
        setSubscriptionPlans(data.plans || []);
        // Update default amount based on plan
        const monthlyPlan = data.plans?.find(p => p.id === 'monthly');
        if (monthlyPlan) {
          setAccountFormData(prev => ({ ...prev, subscription_amount: monthlyPlan.amount }));
        }
      }
    } catch (error) {
      console.error('Error fetching subscription plans:', error);
    }
  };
  
  const fetchAllPayments = async () => {
    try {
      const response = await fetch(`${API_URL}/api/garages/payments/all`);
      const data = await response.json();
      if (data.success) {
        setAllPayments(data.payments || []);
      }
    } catch (error) {
      console.error('Error fetching payments:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      address: '',
      location: '',
      contact_number: '',
      email: '',
      capacity: 10,
      services_offered: [],
      working_hours: '9:00 AM - 7:00 PM',
      manager_name: '',
      is_active: true
    });
    setEditingGarage(null);
  };

  const resetAccountForm = () => {
    setAccountFormData({
      name: '',
      email: '',
      password: '',
      auto_generate_password: false,
      send_email: true,
      mobile: '',
      address: '',
      city: 'Bangalore',
      gst_number: '',
      owner_name: '',
      owner_pin: '1234',
      subscription_plan: 'monthly',
      subscription_amount: subscriptionPlans.find(p => p.id === 'monthly')?.amount || 2999
    });
  };
  
  const resetPaymentForm = () => {
    setPaymentFormData({
      amount: '',
      payment_date: new Date().toISOString().split('T')[0],
      payment_method: 'offline',
      reference_number: '',
      notes: '',
      extend_subscription: true,
      extension_days: 30
    });
    setSelectedGarage(null);
  };
  
  const handlePlanChange = (planId) => {
    const plan = subscriptionPlans.find(p => p.id === planId);
    if (plan) {
      setAccountFormData({
        ...accountFormData,
        subscription_plan: planId,
        subscription_amount: plan.amount
      });
    }
  };

  const handleEdit = (garage) => {
    setEditingGarage(garage);
    setFormData({
      name: garage.name,
      address: garage.address,
      location: garage.location,
      contact_number: garage.contact_number,
      email: garage.email || '',
      capacity: garage.capacity,
      services_offered: garage.services_offered || [],
      working_hours: garage.working_hours,
      manager_name: garage.manager_name || '',
      is_active: garage.is_active
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.address || !formData.location || !formData.contact_number) {
      toast({
        title: 'Validation Error',
        description: 'Please fill all required fields',
        variant: 'destructive'
      });
      return;
    }

    try {
      if (editingGarage) {
        await garageService.update(editingGarage.id, formData);
        toast({
          title: 'Success',
          description: 'Garage updated successfully'
        });
      } else {
        await garageService.create(formData);
        toast({
          title: 'Success',
          description: 'Garage added successfully'
        });
      }
      
      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.detail || 'Failed to save garage',
        variant: 'destructive'
      });
    }
  };

  const handleCreateAccount = async (e) => {
    e.preventDefault();
    
    if (!accountFormData.name || !accountFormData.email || 
        !accountFormData.mobile || !accountFormData.address || !accountFormData.owner_name) {
      toast({
        title: 'Validation Error',
        description: 'Please fill all required fields',
        variant: 'destructive'
      });
      return;
    }

    if (!accountFormData.auto_generate_password && (!accountFormData.password || accountFormData.password.length < 6)) {
      toast({
        title: 'Validation Error',
        description: 'Password must be at least 6 characters (or enable auto-generate)',
        variant: 'destructive'
      });
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/garages/accounts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(accountFormData)
      });
      
      const data = await response.json();
      
      if (data.success) {
        let message = `Garage account created! Login email: ${accountFormData.email}`;
        if (data.password) {
          message += `\nGenerated Password: ${data.password}`;
        }
        if (data.email_sent) {
          message += '\nCredentials sent via email.';
        }
        
        toast({
          title: 'Success',
          description: message
        });
        setIsAccountDialogOpen(false);
        resetAccountForm();
        fetchData();
        fetchGarageAccounts();
      } else {
        toast({
          title: 'Error',
          description: data.detail || 'Failed to create garage account',
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to create garage account',
        variant: 'destructive'
      });
    }
  };
  
  const handleRecordPayment = async (e) => {
    e.preventDefault();
    
    if (!selectedGarage || !paymentFormData.amount) {
      toast({
        title: 'Validation Error',
        description: 'Please select a garage and enter amount',
        variant: 'destructive'
      });
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/garages/accounts/${selectedGarage.id}/payments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...paymentFormData,
          garage_id: selectedGarage.id,
          amount: parseFloat(paymentFormData.amount)
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast({
          title: 'Success',
          description: data.message
        });
        setIsPaymentDialogOpen(false);
        resetPaymentForm();
        fetchData();
        fetchGarageAccounts();
        fetchAllPayments();
      } else {
        toast({
          title: 'Error',
          description: data.detail || 'Failed to record payment',
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to record payment',
        variant: 'destructive'
      });
    }
  };
  
  const handleUpdatePlan = async (e) => {
    e.preventDefault();
    
    if (!editingPlan) return;
    
    try {
      const response = await fetch(`${API_URL}/api/garages/subscription-plans/${editingPlan.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(planFormData)
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast({
          title: 'Success',
          description: 'Subscription plan updated'
        });
        setIsPlanDialogOpen(false);
        setEditingPlan(null);
        fetchSubscriptionPlans();
      } else {
        toast({
          title: 'Error',
          description: data.detail || 'Failed to update plan',
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update plan',
        variant: 'destructive'
      });
    }
  };
  
  const openPaymentDialog = (garage) => {
    setSelectedGarage(garage);
    setPaymentFormData({
      ...paymentFormData,
      amount: garage.subscription?.amount || 2999
    });
    setIsPaymentDialogOpen(true);
  };
  
  const openEditPlanDialog = (plan) => {
    setEditingPlan(plan);
    setPlanFormData({
      plan_name: plan.name,
      amount: plan.amount,
      duration_days: plan.duration_days,
      features: plan.features || []
    });
    setIsPlanDialogOpen(true);
  };
  
  const getSubscriptionStatus = (subscription) => {
    if (!subscription) return { status: 'none', color: 'gray', text: 'No Plan' };
    
    const endDate = new Date(subscription.end_date);
    const today = new Date();
    const daysLeft = Math.ceil((endDate - today) / (1000 * 60 * 60 * 24));
    
    if (daysLeft < 0) return { status: 'expired', color: 'red', text: 'Expired', daysLeft: 0 };
    if (daysLeft <= 7) return { status: 'expiring', color: 'yellow', text: `${daysLeft}d left`, daysLeft };
    return { status: 'active', color: 'green', text: `${daysLeft}d left`, daysLeft };
  };

  const handleDelete = async (garageId, garageName) => {
    if (!window.confirm(`Are you sure you want to delete ${garageName}?`)) {
      return;
    }

    try {
      await garageService.delete(garageId);
      toast({
        title: 'Success',
        description: 'Garage deleted successfully'
      });
      fetchData();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete garage',
        variant: 'destructive'
      });
    }
  };

  const handleDeleteAccount = async (garageId, garageName) => {
    if (!window.confirm(`Are you sure you want to delete the account for ${garageName}? This will revoke their login access.`)) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/garages/accounts/${garageId}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      
      if (data.success) {
        toast({
          title: 'Success',
          description: 'Garage account deleted successfully'
        });
        fetchData();
        fetchGarageAccounts();
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete garage account',
        variant: 'destructive'
      });
    }
  };

  const handleToggleStatus = async (garageId) => {
    try {
      await garageService.toggleStatus(garageId);
      toast({
        title: 'Success',
        description: 'Garage status updated'
      });
      fetchData();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update status',
        variant: 'destructive'
      });
    }
  };

  const handleToggleAccountStatus = async (garageId) => {
    try {
      const response = await fetch(`${API_URL}/api/garages/accounts/${garageId}/toggle-status`, {
        method: 'PATCH'
      });
      const data = await response.json();
      
      if (data.success) {
        toast({
          title: 'Success',
          description: `Garage account ${data.is_active ? 'activated' : 'deactivated'}`
        });
        fetchData();
        fetchGarageAccounts();
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update account status',
        variant: 'destructive'
      });
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent"></div>
        <p className="mt-4 text-gray-600">Loading garages...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Tabs defaultValue="accounts" className="w-full">
        <TabsList className="grid w-full grid-cols-4 mb-4">
          <TabsTrigger value="accounts" className="flex items-center gap-2">
            <Key className="w-4 h-4" /> Garage Accounts
          </TabsTrigger>
          <TabsTrigger value="subscriptions" className="flex items-center gap-2">
            <CreditCard className="w-4 h-4" /> Subscriptions
          </TabsTrigger>
          <TabsTrigger value="payments" className="flex items-center gap-2">
            <IndianRupee className="w-4 h-4" /> Payments
          </TabsTrigger>
          <TabsTrigger value="locations" className="flex items-center gap-2">
            <MapPin className="w-4 h-4" /> Garage Locations
          </TabsTrigger>
        </TabsList>

        {/* Garage Accounts Tab (Propitstop Lite) */}
        <TabsContent value="accounts">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <div>
                  <span>Garage Accounts</span>
                  <p className="text-sm font-normal text-gray-500 mt-1">
                    Create login accounts for garages to use Pitcrew GMS
                  </p>
                </div>
                <Dialog open={isAccountDialogOpen} onOpenChange={setIsAccountDialogOpen}>
                  <DialogTrigger asChild>
                    <Button onClick={resetAccountForm} className="bg-orange-600 hover:bg-orange-700">
                      <Plus className="w-4 h-4 mr-2" />
                      Create Garage Account
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2">
                        <Building2 className="w-5 h-5" />
                        Create Garage Account (Pitcrew GMS)
                      </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreateAccount} className="space-y-4">
                      <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-sm">
                        <p className="text-orange-800">
                          This creates a login account for the garage to access <strong>Pitcrew GMS</strong> 
                          (Garage Management System) at <code>/garage</code>
                        </p>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Garage Name *</Label>
                          <Input
                            value={accountFormData.name}
                            onChange={(e) => setAccountFormData({ ...accountFormData, name: e.target.value })}
                            placeholder="e.g., ABC Auto Care"
                            required
                          />
                        </div>
                        <div>
                          <Label>Owner Name *</Label>
                          <Input
                            value={accountFormData.owner_name}
                            onChange={(e) => setAccountFormData({ ...accountFormData, owner_name: e.target.value })}
                            placeholder="Owner's full name"
                            required
                          />
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Login Email *</Label>
                          <Input
                            type="email"
                            value={accountFormData.email}
                            onChange={(e) => setAccountFormData({ ...accountFormData, email: e.target.value })}
                            placeholder="garage@example.com"
                            required
                          />
                        </div>
                        <div>
                          <Label>Mobile Number *</Label>
                          <Input
                            value={accountFormData.mobile}
                            onChange={(e) => setAccountFormData({ ...accountFormData, mobile: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                            placeholder="9876543210"
                            maxLength={10}
                            required
                          />
                        </div>
                      </div>

                      {/* Password Section */}
                      <div className="border rounded-lg p-4 space-y-3">
                        <div className="flex items-center justify-between">
                          <Label className="font-semibold">Password Settings</Label>
                          <div className="flex items-center gap-2">
                            <Switch
                              checked={accountFormData.auto_generate_password}
                              onCheckedChange={(checked) => setAccountFormData({ ...accountFormData, auto_generate_password: checked })}
                            />
                            <span className="text-sm text-gray-600">Auto-generate</span>
                          </div>
                        </div>
                        
                        {!accountFormData.auto_generate_password && (
                          <div>
                            <Label>Password *</Label>
                            <Input
                              type="password"
                              value={accountFormData.password}
                              onChange={(e) => setAccountFormData({ ...accountFormData, password: e.target.value })}
                              placeholder="Min 6 characters"
                              minLength={6}
                            />
                          </div>
                        )}
                        
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={accountFormData.send_email}
                            onCheckedChange={(checked) => setAccountFormData({ ...accountFormData, send_email: checked })}
                          />
                          <span className="text-sm text-gray-600">
                            <Send className="w-4 h-4 inline mr-1" />
                            Email credentials to garage
                          </span>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>City</Label>
                          <Input
                            value={accountFormData.city}
                            onChange={(e) => setAccountFormData({ ...accountFormData, city: e.target.value })}
                            placeholder="Bangalore"
                          />
                        </div>
                        <div>
                          <Label>Owner PIN (4-6 digits)</Label>
                          <Input
                            value={accountFormData.owner_pin}
                            onChange={(e) => setAccountFormData({ ...accountFormData, owner_pin: e.target.value.replace(/\D/g, '').slice(0, 6) })}
                            placeholder="1234"
                            maxLength={6}
                          />
                        </div>
                      </div>

                      <div>
                        <Label>Address *</Label>
                        <Textarea
                          value={accountFormData.address}
                          onChange={(e) => setAccountFormData({ ...accountFormData, address: e.target.value })}
                          placeholder="Complete garage address"
                          rows={2}
                          required
                        />
                      </div>

                      {/* Subscription Section */}
                      <div className="border rounded-lg p-4 space-y-3 bg-blue-50">
                        <Label className="font-semibold text-blue-800">Subscription Plan</Label>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>Plan</Label>
                            <Select
                              value={accountFormData.subscription_plan}
                              onValueChange={handlePlanChange}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {subscriptionPlans.map(plan => (
                                  <SelectItem key={plan.id} value={plan.id}>
                                    {plan.name} - ₹{plan.amount}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label>Amount (₹)</Label>
                            <Input
                              type="number"
                              value={accountFormData.subscription_amount}
                              onChange={(e) => setAccountFormData({ ...accountFormData, subscription_amount: parseFloat(e.target.value) || 0 })}
                              placeholder="2999"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="flex justify-end gap-2 pt-4">
                        <Button type="button" variant="outline" onClick={() => setIsAccountDialogOpen(false)}>
                          <X className="w-4 h-4 mr-2" /> Cancel
                        </Button>
                        <Button type="submit" className="bg-orange-600 hover:bg-orange-700">
                          <Key className="w-4 h-4 mr-2" /> Create Account
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {garageAccounts.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Building2 className="w-16 h-16 mx-auto mb-4 opacity-30" />
                  <p className="text-lg font-medium">No garage accounts yet</p>
                  <p className="text-sm">Create accounts for garages to use Pitcrew GMS</p>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {garageAccounts.map((account) => {
                    const subStatus = getSubscriptionStatus(account.subscription);
                    return (
                      <Card key={account.id} className="hover:shadow-lg transition-all border-l-4 border-l-orange-500">
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <h3 className="font-bold text-lg">{account.name}</h3>
                              <p className="text-sm text-gray-500">{account.owner_name}</p>
                            </div>
                            <div className="flex flex-col gap-1 items-end">
                              <Badge className={account.is_active ? 'bg-green-500' : 'bg-red-500'}>
                                {account.is_active ? 'Active' : 'Inactive'}
                              </Badge>
                              <Badge variant="outline" className={
                                subStatus.color === 'green' ? 'border-green-500 text-green-600' :
                                subStatus.color === 'yellow' ? 'border-yellow-500 text-yellow-600' :
                                subStatus.color === 'red' ? 'border-red-500 text-red-600' :
                                'border-gray-400 text-gray-500'
                              }>
                                {subStatus.text}
                              </Badge>
                            </div>
                          </div>
                          
                          <div className="space-y-2 text-sm mb-4">
                            <div className="flex items-center gap-2 text-gray-600">
                              <Mail className="w-4 h-4" />
                              <span className="truncate">{account.email}</span>
                            </div>
                            <div className="flex items-center gap-2 text-gray-600">
                              <Phone className="w-4 h-4" />
                              <span>{account.mobile}</span>
                            </div>
                            <div className="flex items-start gap-2 text-gray-600">
                              <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                              <span className="line-clamp-2">{account.address}</span>
                            </div>
                            {account.subscription && (
                              <div className="flex items-center gap-2 text-gray-600">
                                <CreditCard className="w-4 h-4" />
                                <span>
                                  {account.subscription.plan?.charAt(0).toUpperCase() + account.subscription.plan?.slice(1)} - 
                                  ₹{account.subscription.total_paid || 0} paid
                                </span>
                              </div>
                            )}
                          </div>

                          <div className="flex gap-2 flex-wrap">
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => window.open('/garage/login', '_blank')}
                              className="flex-1"
                            >
                              <ExternalLink className="w-3 h-3 mr-1" /> Portal
                            </Button>
                            <Button 
                              size="sm"
                              onClick={() => openPaymentDialog(account)}
                              className="flex-1 bg-green-600 hover:bg-green-700"
                            >
                              <IndianRupee className="w-3 h-3 mr-1" /> Payment
                            </Button>
                            <Button 
                              size="sm" 
                              variant={account.is_active ? "outline" : "default"}
                              onClick={() => handleToggleAccountStatus(account.id)}
                            >
                              {account.is_active ? 'Deactivate' : 'Activate'}
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleDeleteAccount(account.id, account.name)}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Subscriptions Tab */}
        <TabsContent value="subscriptions">
          <div className="space-y-6">
            {/* Subscription Plans */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Subscription Plans</span>
                  <Button variant="outline" onClick={fetchSubscriptionPlans}>
                    <RefreshCw className="w-4 h-4 mr-2" /> Refresh
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-3 gap-4">
                  {subscriptionPlans.map(plan => (
                    <Card key={plan.id} className="relative">
                      <CardContent className="p-6">
                        <h3 className="text-xl font-bold text-center mb-2">{plan.name}</h3>
                        <p className="text-3xl font-bold text-center text-orange-600 mb-1">
                          ₹{plan.amount}
                        </p>
                        <p className="text-center text-gray-500 mb-4">{plan.duration_days} days</p>
                        
                        <ul className="space-y-2 mb-4">
                          {plan.features?.map((feature, idx) => (
                            <li key={idx} className="flex items-center gap-2 text-sm">
                              <CheckCircle className="w-4 h-4 text-green-500" />
                              {feature}
                            </li>
                          ))}
                        </ul>
                        
                        <Button 
                          variant="outline" 
                          className="w-full"
                          onClick={() => openEditPlanDialog(plan)}
                        >
                          <Edit className="w-4 h-4 mr-2" /> Edit Plan
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
            
            {/* Subscription Overview */}
            <Card>
              <CardHeader>
                <CardTitle>Subscription Overview</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-4 gap-4 mb-6">
                  <div className="bg-green-50 p-4 rounded-lg text-center">
                    <p className="text-3xl font-bold text-green-600">
                      {garageAccounts.filter(a => getSubscriptionStatus(a.subscription).status === 'active').length}
                    </p>
                    <p className="text-sm text-green-700">Active</p>
                  </div>
                  <div className="bg-yellow-50 p-4 rounded-lg text-center">
                    <p className="text-3xl font-bold text-yellow-600">
                      {garageAccounts.filter(a => getSubscriptionStatus(a.subscription).status === 'expiring').length}
                    </p>
                    <p className="text-sm text-yellow-700">Expiring Soon</p>
                  </div>
                  <div className="bg-red-50 p-4 rounded-lg text-center">
                    <p className="text-3xl font-bold text-red-600">
                      {garageAccounts.filter(a => getSubscriptionStatus(a.subscription).status === 'expired').length}
                    </p>
                    <p className="text-sm text-red-700">Expired</p>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-lg text-center">
                    <p className="text-3xl font-bold text-blue-600">
                      ₹{allPayments.reduce((sum, p) => sum + (p.amount || 0), 0).toLocaleString()}
                    </p>
                    <p className="text-sm text-blue-700">Total Revenue</p>
                  </div>
                </div>
                
                {/* Expiring/Expired list */}
                <div className="space-y-2">
                  <h4 className="font-semibold flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-yellow-500" />
                    Attention Required
                  </h4>
                  {garageAccounts
                    .filter(a => ['expiring', 'expired'].includes(getSubscriptionStatus(a.subscription).status))
                    .map(account => {
                      const subStatus = getSubscriptionStatus(account.subscription);
                      return (
                        <div key={account.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div>
                            <p className="font-medium">{account.name}</p>
                            <p className="text-sm text-gray-500">{account.email}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className={
                              subStatus.status === 'expired' ? 'border-red-500 text-red-600' : 'border-yellow-500 text-yellow-600'
                            }>
                              {subStatus.text}
                            </Badge>
                            <Button size="sm" onClick={() => openPaymentDialog(account)} className="bg-green-600 hover:bg-green-700">
                              <IndianRupee className="w-3 h-3 mr-1" /> Renew
                            </Button>
                          </div>
                        </div>
                      );
                    })
                  }
                  {garageAccounts.filter(a => ['expiring', 'expired'].includes(getSubscriptionStatus(a.subscription).status)).length === 0 && (
                    <p className="text-gray-500 text-sm">No subscriptions need attention</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Payments Tab */}
        <TabsContent value="payments">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Payment History</span>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={fetchAllPayments}>
                    <RefreshCw className="w-4 h-4 mr-2" /> Refresh
                  </Button>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-blue-50 p-4 rounded-lg mb-6 flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-700">Total Revenue</p>
                  <p className="text-3xl font-bold text-blue-800">
                    ₹{allPayments.reduce((sum, p) => sum + (p.amount || 0), 0).toLocaleString()}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-blue-700">Total Payments</p>
                  <p className="text-3xl font-bold text-blue-800">{allPayments.length}</p>
                </div>
              </div>
              
              {allPayments.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <IndianRupee className="w-16 h-16 mx-auto mb-4 opacity-30" />
                  <p className="text-lg font-medium">No payments recorded yet</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Date</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Garage</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Amount</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Method</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Reference</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Notes</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {allPayments.map(payment => (
                        <tr key={payment.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm">
                            {new Date(payment.payment_date).toLocaleDateString()}
                          </td>
                          <td className="px-4 py-3 text-sm font-medium">{payment.garage_name}</td>
                          <td className="px-4 py-3 text-sm font-semibold text-green-600">
                            ₹{payment.amount?.toLocaleString()}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <Badge variant="outline">{payment.payment_method}</Badge>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500">
                            {payment.reference_number || '-'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500 max-w-[200px] truncate">
                            {payment.notes || '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Garage Locations Tab */}
        <TabsContent value="locations">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Garage Locations</span>
                <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                  <DialogTrigger asChild>
                    <Button onClick={resetForm}>
                      <Plus className="w-4 h-4 mr-2" />
                      Add Location
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>
                        {editingGarage ? 'Edit Garage' : 'Add New Garage Location'}
                      </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Garage Name *</Label>
                          <Input
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            placeholder="e.g., Propitstop Koramangala"
                            required
                          />
                        </div>
                        <div>
                          <Label>Location *</Label>
                          <Input
                            value={formData.location}
                            onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                            placeholder="e.g., Koramangala"
                            required
                          />
                        </div>
                      </div>
                      
                      <div>
                        <Label>Address *</Label>
                        <Textarea
                          value={formData.address}
                          onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                          placeholder="Complete address"
                          rows={2}
                          required
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Contact Number *</Label>
                          <Input
                            value={formData.contact_number}
                            onChange={(e) => setFormData({ ...formData, contact_number: e.target.value })}
                            placeholder="9876543210"
                            required
                          />
                        </div>
                        <div>
                          <Label>Email</Label>
                          <Input
                            type="email"
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            placeholder="garage@propitstop.in"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Capacity (Cars)</Label>
                          <Input
                            type="number"
                            value={formData.capacity}
                            onChange={(e) => setFormData({ ...formData, capacity: parseInt(e.target.value) || 0 })}
                            min="1"
                          />
                        </div>
                        <div>
                          <Label>Working Hours</Label>
                          <Input
                            value={formData.working_hours}
                            onChange={(e) => setFormData({ ...formData, working_hours: e.target.value })}
                            placeholder="9:00 AM - 7:00 PM"
                          />
                        </div>
                      </div>

                      <div>
                        <Label>Manager Name</Label>
                        <Input
                          value={formData.manager_name}
                          onChange={(e) => setFormData({ ...formData, manager_name: e.target.value })}
                          placeholder="Manager name"
                        />
                      </div>

                      <div className="flex items-center space-x-2">
                        <Switch
                          checked={formData.is_active}
                          onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                        />
                        <Label>Active Status</Label>
                      </div>

                      <div className="flex justify-end gap-2 pt-4">
                        <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                          <X className="w-4 h-4 mr-2" /> Cancel
                        </Button>
                        <Button type="submit">
                          <Save className="w-4 h-4 mr-2" /> Save Garage
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {garages.map((garage) => (
                  <Card key={garage.id} className="hover:shadow-lg transition-all">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <h3 className="font-bold text-lg">{garage.name}</h3>
                        <Badge className={garage.is_active ? 'bg-green-500' : 'bg-red-500'}>
                          {garage.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </div>
                      
                      <div className="space-y-2 text-sm mb-4">
                        <div className="flex items-start gap-2 text-gray-600">
                          <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                          <span>{garage.address}</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-600">
                          <Phone className="w-4 h-4" />
                          <span>{garage.contact_number}</span>
                        </div>
                        {garage.email && (
                          <div className="flex items-center gap-2 text-gray-600">
                            <Mail className="w-4 h-4" />
                            <span className="truncate">{garage.email}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-2 text-gray-600">
                          <Users className="w-4 h-4" />
                          <span>Capacity: {garage.capacity} cars</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-600">
                          <Clock className="w-4 h-4" />
                          <span>{garage.working_hours}</span>
                        </div>
                      </div>

                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => handleEdit(garage)} className="flex-1">
                          <Edit className="w-3 h-3 mr-1" /> Edit
                        </Button>
                        <Button 
                          size="sm" 
                          variant={garage.is_active ? "outline" : "default"}
                          onClick={() => handleToggleStatus(garage.id)}
                          className="flex-1"
                        >
                          {garage.is_active ? 'Deactivate' : 'Activate'}
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleDelete(garage.id, garage.name)}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      
      {/* Payment Dialog */}
      <Dialog open={isPaymentDialogOpen} onOpenChange={setIsPaymentDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <IndianRupee className="w-5 h-5 text-green-600" />
              Record Payment
            </DialogTitle>
          </DialogHeader>
          {selectedGarage && (
            <form onSubmit={handleRecordPayment} className="space-y-4">
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="font-semibold">{selectedGarage.name}</p>
                <p className="text-sm text-gray-500">{selectedGarage.email}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Amount (₹) *</Label>
                  <Input
                    type="number"
                    value={paymentFormData.amount}
                    onChange={(e) => setPaymentFormData({ ...paymentFormData, amount: e.target.value })}
                    placeholder="2999"
                    required
                  />
                </div>
                <div>
                  <Label>Payment Date *</Label>
                  <Input
                    type="date"
                    value={paymentFormData.payment_date}
                    onChange={(e) => setPaymentFormData({ ...paymentFormData, payment_date: e.target.value })}
                    required
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Payment Method</Label>
                  <Select
                    value={paymentFormData.payment_method}
                    onValueChange={(v) => setPaymentFormData({ ...paymentFormData, payment_method: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="offline">Cash/Offline</SelectItem>
                      <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                      <SelectItem value="upi">UPI</SelectItem>
                      <SelectItem value="cheque">Cheque</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Reference Number</Label>
                  <Input
                    value={paymentFormData.reference_number}
                    onChange={(e) => setPaymentFormData({ ...paymentFormData, reference_number: e.target.value })}
                    placeholder="Transaction ID"
                  />
                </div>
              </div>
              
              <div>
                <Label>Notes</Label>
                <Input
                  value={paymentFormData.notes}
                  onChange={(e) => setPaymentFormData({ ...paymentFormData, notes: e.target.value })}
                  placeholder="Optional notes"
                />
              </div>
              
              <div className="border rounded-lg p-3 space-y-2">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={paymentFormData.extend_subscription}
                    onCheckedChange={(checked) => setPaymentFormData({ ...paymentFormData, extend_subscription: checked })}
                  />
                  <span className="text-sm">Extend subscription</span>
                </div>
                {paymentFormData.extend_subscription && (
                  <div className="flex items-center gap-2">
                    <Label className="text-sm">Extend by</Label>
                    <Input
                      type="number"
                      value={paymentFormData.extension_days}
                      onChange={(e) => setPaymentFormData({ ...paymentFormData, extension_days: parseInt(e.target.value) || 30 })}
                      className="w-20"
                    />
                    <span className="text-sm text-gray-500">days</span>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsPaymentDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-green-600 hover:bg-green-700">
                  <IndianRupee className="w-4 h-4 mr-2" /> Record Payment
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Edit Plan Dialog */}
      <Dialog open={isPlanDialogOpen} onOpenChange={setIsPlanDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5" />
              Edit Subscription Plan
            </DialogTitle>
          </DialogHeader>
          {editingPlan && (
            <form onSubmit={handleUpdatePlan} className="space-y-4">
              <div>
                <Label>Plan Name</Label>
                <Input
                  value={planFormData.plan_name}
                  onChange={(e) => setPlanFormData({ ...planFormData, plan_name: e.target.value })}
                  placeholder="Monthly"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Amount (₹)</Label>
                  <Input
                    type="number"
                    value={planFormData.amount}
                    onChange={(e) => setPlanFormData({ ...planFormData, amount: parseFloat(e.target.value) || 0 })}
                    placeholder="2999"
                  />
                </div>
                <div>
                  <Label>Duration (Days)</Label>
                  <Input
                    type="number"
                    value={planFormData.duration_days}
                    onChange={(e) => setPlanFormData({ ...planFormData, duration_days: parseInt(e.target.value) || 30 })}
                    placeholder="30"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsPlanDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit">
                  <Save className="w-4 h-4 mr-2" /> Save Changes
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GaragesManager;
